import {
  ChatMessage,
  ChatCompletionResponse,
  SiteBriefChatMetadata,
  MessageMentionsType,
  MessageDecisionReferenceType,
  MentionItemType,
  OfferPlanUpgradeType,
  MessageRouterType,
  VibeBlockDesignWizardQuestionType,
  VibeBlockDesignWizardAnswerType
} from './api.types';
import { sendSiteBriefChatRequest, sendChatRequest, fetchAvailableTools, createSession } from './api';
import { useSession, type SessionBasic } from './components/SessionDropdown';

type MessageMeta = {
  timestamp?: string;
  duration?: number;
  isLiked?: boolean;
  isDisliked?: boolean;
  role?: string;
  mentions?: MessageMentionsType;
  router?: MessageRouterType;
  isRouterLoading?: boolean;
  decisions?: MessageDecisionReferenceType[];
  offer_plan_upgrade?: OfferPlanUpgradeType;
  vibe_block_design_wizard_question?: VibeBlockDesignWizardQuestionType;
  vibe_block_design_wizard_answer?: VibeBlockDesignWizardAnswerType;
  vibeBlockDesignWizardAnswerStatus?: 'pending' | 'confirmed' | 'failed';
  mentionWebsiteName?: string;
};

export type ChatUserResponseOptions = {
  clearUserInput?: boolean;
  includeMentions?: boolean;
};

// Image data for display in chat
export type ImageData = {
  url: string; // base64 data URL for display
  name: string;
  size: string;
};

export const MAX_IMAGE_UPLOAD_BYTES = 5 * 1024 * 1024;
export const MAX_IMAGE_UPLOAD_SIZE_LABEL = "5 MB";

export type ChatFlowMessage = {
  id: string;
  type: 'system' | 'question';
  content: string;
  field?: string;
  suggestions?: string[];
  isFinished?: boolean;
} & MessageMeta;

export type UserMessage = {
  id: string;
  type: 'user';
  content: string;
  images?: ImageData[]; // Images attached to the message
} & MessageMeta;

export type HelpMessage = {
  id: string;
  type: 'help';
  content: string;
  suggestions: string[];
} & MessageMeta;

export type ProceedMessage = {
  id: string;
  type: 'system';
  content: string;
} & MessageMeta;

export type ErrorMessage = {
  id: string;
  type: 'error';
  content: string;
  errorCode?: string;
} & MessageMeta;

export type Message = ChatFlowMessage | UserMessage | HelpMessage | ProceedMessage | ErrorMessage;

export type ChatActions = {
  setMessages: (updater: (messages: Message[]) => Message[]) => void;
  getMessages: () => Message[];
  setUserInput: (input: string) => void;
  setCurrentStep: (updater: (step: number) => number) => void;
  updateBrief: (field: string, value: string) => void;
  setSendButtonAnimation: (animate: boolean) => void;
  setShowHelpSuggestions: (show: boolean) => void;
  setShowCompletionButtons: (show: boolean) => void;
  setIsLoading?: (loading: boolean) => void;
  focusInput?: () => void;
  getAuthToken?: () => string | null;
  getAllowedTools?: () => string[];
  getSessionId?: () => string | undefined;
  getSessionType?: () => string | undefined;
  getSiteBrief?: () => SiteBriefChatMetadata | null;
  setSiteBrief?: (brief: SiteBriefChatMetadata, sessionId: string) => void;
  getLanguage?: () => string;
  setSessionFromResponse?: (session: SessionBasic) => void;
  setLastToolsCalls?: (calls: ChatCompletionResponse['lastToolsCalls']) => void;
  setTotalTokens?: (tokens: number) => void;
  setSiteBriefTask?: (task: ChatCompletionResponse['task'], sessionId: string) => void;
  getSelectedMentionWebsite?: () => { id: string; name: string; status?: string; domainLabel?: string } | null;
  getSelectedMentionPages?: () => {
    id: string;
    name: string;
    label?: string;
    lang?: string;
    lang_group?: string;
    is_home_page?: boolean;
  }[];
  getSelectedMentionBlock?: () => MentionItemType | null;
  getSelectedMentionElements?: () => MentionItemType[];
};

export const chatFlow: ChatFlowMessage[] = [
  {
    id: '0',
    type: 'system',
    //content: 'Welcome! I\'m here to answer all your questions. Let\'s get started!',
    //content: 'Welcome! I\'m Webby, your assistant, I\'m here to answer all your questions. Let\'s get started!',
    content: 'Hello! How can I help? I\'m Evo, your assistant. This message will not be included in your session. Please start a new session to begin your website creation journey!',
    isFinished: false
  }
];

export function getWritingSuggestions(currentStep: number): string[] {

  console.log('getWritingSuggestions :: currentStep', currentStep);

  if (currentStep < chatFlow.length) {
    const currentQuestion = chatFlow[currentStep];

    if (currentQuestion.suggestions) {
      return currentQuestion.suggestions;
    }

    switch (currentQuestion.field) {
      case 'businessName':
        return [
          'Use your actual business name',
          'Add a distinctive modifier',
          'Include your industry',
          'Keep it short and memorable'
        ];
      case 'businessType':
        return [
          'Health and wellness',
          'Nutritional supplements',
          'Direct-to-consumer vitamins',
          'Subscription vitamin service'
        ];
      case 'product':
        return [
          'List your flagship products',
          'Mention product categories',
          'Highlight specialty formulations',
          'Include subscription options'
        ];
      case 'targetAudience':
        return [
          'Health-conscious adults',
          'Athletes and fitness enthusiasts',
          'Seniors looking for wellness',
          'Parents seeking children\'s supplements'
        ];
      case 'serviceOffering':
        return [
          'Product catalog with filtering',
          'Personalized recommendations',
          'Educational content and blog',
          'Customer reviews and testimonials'
        ];
      case 'websiteGoal':
        return [
          'Drive online sales',
          'Educate visitors about nutrition',
          'Build subscription customer base',
          'Establish brand credibility'
        ];
      default:
        return [
          'I need more specific details',
          'Please explain in 1-2 sentences',
          'Could you give an example?',
          'Let me try a different approach'
        ];
    }
  }

  return [
    'I need more specific details',
    'Please explain in 1-2 sentences',
    'Could you give an example?',
    'Let me try a different approach'
  ];
}

// Utility function to simulate API calls with possible errors
const simulateApiCall = async (data: any, errorRate = 0.2): Promise<any> => {
  // Simulate API processing time
  await new Promise(resolve => setTimeout(resolve, 300));

  // Randomly fail based on errorRate (20% by default)
  if (Math.random() < errorRate) {
    throw new Error('Failed to process your response. The server is currently experiencing issues. Please try again.');
  }

  // Return processed data
  return data;
};

export const handleUserResponse = async (
  actions: ChatActions,
  currentStep: number,
  userInput: string,
  response: string | null = null,
  images: ImageData[] = [],
  vibeBlockDesignWizardAnswer?: VibeBlockDesignWizardAnswerType,
  options: ChatUserResponseOptions = {}
) => {

  console.log('handleUserResponse :: actions', actions);
  console.log('handleUserResponse :: currentStep', currentStep);
  console.log('handleUserResponse :: userInput', userInput);
  console.log('handleUserResponse :: response', response);
  console.log('handleUserResponse :: images', images.length);

  const originatingSessionId = actions.getSessionId?.();
  const isSiteBrief = actions.getSessionType?.() === 'vibe_site';
  const isCurrentSession = () => !originatingSessionId || actions.getSessionId?.() === originatingSessionId;

  // Get current messages
  const currentMessagesList = actions.getMessages();
  console.log('handleUserResponse :: currentMessagesList', currentMessagesList);

  // Get API requirements
  const authToken = actions.getAuthToken?.() || localStorage.getItem('authToken');

  if (!authToken) {
    console.error('No auth token available for API request');
    const errorMessage: ErrorMessage = {
      id: `error-auth-${Date.now()}`,
      type: 'error',
      content: 'Authentication error. Please log in and try again.',
      errorCode: 'AUTH_ERROR'
    };
    actions.setMessages((prev: Message[]) => [...prev, errorMessage]);
    return;
  }

  // Trigger send button animation
  actions.setSendButtonAnimation(true);
  setTimeout(() => actions.setSendButtonAnimation(false), 400);

  const currentQuestion = chatFlow[currentStep] as ChatFlowMessage | undefined;
  const selectedMentionWebsite = actions.getSelectedMentionWebsite ? actions.getSelectedMentionWebsite() : null;
  const selectedMentionPages = actions.getSelectedMentionPages ? actions.getSelectedMentionPages() : [];
  const selectedMentionBlock = actions.getSelectedMentionBlock ? actions.getSelectedMentionBlock() : null;
  const selectedMentionElements = actions.getSelectedMentionElements ? actions.getSelectedMentionElements() : [];
  const shouldIncludeMentions = !isSiteBrief && options.includeMentions !== false;
  const mentions = shouldIncludeMentions && (
    selectedMentionWebsite
    || selectedMentionPages.length > 0
    || selectedMentionBlock
    || selectedMentionElements.length > 0
  )
    ? {
        ...(selectedMentionWebsite ? { website: selectedMentionWebsite } : {}),
        ...(selectedMentionPages.length > 0 ? { pages: selectedMentionPages } : {}),
        ...(selectedMentionBlock ? { blocks: [selectedMentionBlock] } : {}),
        ...(selectedMentionElements.length > 0 ? { elements: selectedMentionElements } : {})
      }
    : undefined;
  const userMessage: UserMessage = {
    id: isSiteBrief ? crypto.randomUUID() : `user-${currentStep}`,
    type: 'user',
    content: response || userInput,
    images: !isSiteBrief && images.length > 0 ? images : undefined,
    mentions,
    vibe_block_design_wizard_answer: vibeBlockDesignWizardAnswer,
    vibeBlockDesignWizardAnswerStatus: vibeBlockDesignWizardAnswer ? 'pending' : undefined,
    isRouterLoading: !isSiteBrief,
    mentionWebsiteName: selectedMentionWebsite?.name
  };

  // for error displayed with "Try again" - If the user message already exists, add a retry suffix to the id
  if (currentMessagesList.find(message => message.id === userMessage.id)) {
    userMessage.id += `-retry-${Date.now()}`;
  }

  // Keep only the latest local attempt for an unanswered wizard step. Confirmed answers stay in history.
  actions.setMessages((prev: Message[]) => [
    ...prev.filter((message) => !(
      vibeBlockDesignWizardAnswer
      && message.vibe_block_design_wizard_answer?.wizard_id === vibeBlockDesignWizardAnswer.wizard_id
      && message.vibe_block_design_wizard_answer.step === vibeBlockDesignWizardAnswer.step
      && message.vibeBlockDesignWizardAnswerStatus !== 'confirmed'
    )),
    userMessage
  ]);

  try {
    // Set loading state to true before API call
    if (actions.setIsLoading) {
      actions.setIsLoading(true);
    }

    if (isSiteBrief) {
      const brief = actions.getSiteBrief?.();
      if (!originatingSessionId || !brief || brief.status !== 'gather_requirements') {
        throw new Error('Site brief chat is available only while gathering requirements.');
      }
      const receipt = await sendSiteBriefChatRequest(authToken, originatingSessionId, userMessage.content, brief.version, userMessage.id);
      actions.setSiteBriefTask?.(receipt.task, originatingSessionId);
      if (!isCurrentSession()) return;
      actions.setSiteBrief?.(receipt.site_brief, originatingSessionId);
      actions.setMessages(prev => prev.map(message => message.id === userMessage.id
        ? { ...message, id: receipt.user_message_id, isRouterLoading: false } : message));
      if (options.clearUserInput !== false) actions.setUserInput('');
      actions.setShowHelpSuggestions(false);
      return;
    }

    const inputData = response || userInput;
    const currentField = currentQuestion && currentQuestion.field ? currentQuestion.field : 'Last input';
    console.log(`Processing user input for field: ${currentField}`);

    const buildMessageImages = (msg: Message): ChatMessage['images'] => {
      if (msg.type !== 'user' || !(msg as UserMessage).images || (msg as UserMessage).images!.length === 0) {
        return undefined;
      }

      return (msg as UserMessage).images!.map((img) => ({
        url: img.url,
        name: img.name
      }));
    };

    // Prepare messages for the API
    const apiMessages: ChatMessage[] = currentMessagesList
      .filter(msg => msg.type === 'user' || msg.type === 'system' || msg.type === 'question')
      .filter(msg => !msg.vibe_block_design_wizard_answer || msg.vibeBlockDesignWizardAnswerStatus === 'confirmed')
      .map(msg => ({
        id: msg.id,
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content,
        images: buildMessageImages(msg),
        mentions: msg.type === 'user' ? msg.mentions : undefined,
        vibe_block_design_wizard_question: msg.type !== 'user' ? msg.vibe_block_design_wizard_question : undefined,
        vibe_block_design_wizard_answer: msg.type === 'user' ? msg.vibe_block_design_wizard_answer : undefined
      }));

    // Add the current user message with images
    apiMessages.push({
      id: userMessage.id,
      role: userMessage.type === 'user' ? 'user' : 'assistant',
      content: userMessage.content,
      images: buildMessageImages(userMessage),
      mentions: userMessage.mentions,
      vibe_block_design_wizard_answer: userMessage.vibe_block_design_wizard_answer
    });

    // Call the OpenAI API
    const allowedTools = actions.getAllowedTools ? actions.getAllowedTools() : [];
    let sessionId = actions.getSessionId ? actions.getSessionId() : undefined;
    if (!sessionId) {
      // Auto-create a new session
      const autoName = `Session started at ${new Date().toISOString()}`;
      const language = actions.getLanguage ? actions.getLanguage() : 'en';
      const newSession = await createSession(authToken, autoName, allowedTools, language);
      actions.setSessionFromResponse && actions.setSessionFromResponse(newSession);
      sessionId = newSession.id;
    }
    console.log('Sending to API:', { messages: apiMessages, allowedTools, sessionId });
    const apiResponse = await sendChatRequest(apiMessages, authToken, allowedTools, sessionId as string);

    // Handle API response
    if (apiResponse.status === 'Error') {
      throw new Error(apiResponse.lastReply.content || 'API request failed');
    }

    console.log('API response:', apiResponse);

    actions.setTotalTokens?.(apiResponse.totalTokens);
    if (sessionId) actions.setSiteBriefTask?.(apiResponse.task, sessionId);

    actions.setMessages((prev: Message[]) => prev.map((message) => (
      message.id === userMessage.id
        ? {
            ...message,
            router: apiResponse.router,
            isRouterLoading: false,
            vibeBlockDesignWizardAnswerStatus: message.vibe_block_design_wizard_answer
              ? 'confirmed'
              : message.vibeBlockDesignWizardAnswerStatus
          }
        : message
    )));

    // sessionId is required now; no need to set from response

    // Expose last tools calls to UI if a setter is provided
    if (actions.setLastToolsCalls) {
      actions.setLastToolsCalls(apiResponse.lastToolsCalls || []);
    }

    const replyContent = apiResponse.lastReply?.content ?? '';
    const rawSuggestions = (apiResponse.lastReply as { suggestions?: { options?: string[] } } | undefined)?.suggestions;
    const assistantMessageId = apiResponse.lastReply?.id || `assistant-${Date.now()}`;

    const assistantMessage: Message = {
      id: assistantMessageId,
      type: 'system',
      content: replyContent,
      timestamp: apiResponse.lastReply?.timestamp,
      duration: apiResponse.lastReply?.duration,
      isLiked: apiResponse.lastReply?.is_liked,
      isDisliked: apiResponse.lastReply?.is_disliked,
      role: apiResponse.lastReply?.role,
      decisions: apiResponse.lastReply?.decisions,
      offer_plan_upgrade: apiResponse.lastReply?.offer_plan_upgrade,
      vibe_block_design_wizard_question: apiResponse.lastReply?.vibe_block_design_wizard_question
    };

    actions.setMessages((prev: Message[]) => {
      const filtered = prev.filter(msg => msg.id !== assistantMessageId);
      return [...filtered, assistantMessage];
    });

    const suggestionsOptions = rawSuggestions?.options ?? [];
    const nextQuestion = chatFlow[currentStep + 1];
    const nextQuestionBase: ChatFlowMessage = {
      id: assistantMessageId,
      type: 'question',
      content: assistantMessage.content,
      suggestions: suggestionsOptions,
      isFinished: apiResponse.isConversationEnded,
      timestamp: assistantMessage.timestamp,
      duration: assistantMessage.duration,
      isLiked: assistantMessage.isLiked,
      isDisliked: assistantMessage.isDisliked,
      role: assistantMessage.role,
      vibe_block_design_wizard_question: assistantMessage.vibe_block_design_wizard_question
    };

    if (nextQuestion) {
      chatFlow[currentStep + 1] = {
        ...nextQuestionBase,
        field: nextQuestion.field
      };
    } else {
      chatFlow.push(nextQuestionBase);
    }

    // Update brief data with processed response
    actions.updateBrief(currentField, inputData);

  } catch (error) {
    console.error('Error processing user response:', error);
    if (!isCurrentSession()) return;

    actions.setMessages((prev: Message[]) => prev.map((message) => (
      message.id === userMessage.id
        ? {
            ...message,
            isRouterLoading: false,
            vibeBlockDesignWizardAnswerStatus: message.vibe_block_design_wizard_answer
              ? 'failed'
              : message.vibeBlockDesignWizardAnswerStatus
          }
        : message
    )));

    // Create an error message to display to the user
    const errorMessage: ErrorMessage = {
      id: `error-${Date.now()}`,
      type: 'error',
      content: error instanceof Error
        ? error.message
        : 'Something went wrong. Please try again.',
      errorCode: 'API_ERROR'
    };

    // Add the error message to the chat
    actions.setMessages((prev: Message[]) => [...prev, errorMessage]);

    // Don't proceed to the next step when there's an error
    return;

  } finally {
    // Set loading state to false after API call completes (success or error)
    if (isCurrentSession() && actions.setIsLoading) {
      actions.setIsLoading(false);
    }
  }

  // Move to next step
  actions.setCurrentStep((prev: number) => prev + 1);
  if (options.clearUserInput !== false) {
    actions.setUserInput('');
  }
  actions.setShowHelpSuggestions(false); // Hide help suggestions after submitting
};

export const handleSkipQuestion = (
  actions: ChatActions
) => {

  console.log('handleSkipQuestion :: actions', actions);

  actions.setCurrentStep((prev: number) => prev + 1);
  actions.setUserInput('');
  actions.setShowHelpSuggestions(false);
};

export const handleHelpClick = (
  actions: ChatActions,
  currentStep: number
) => {

  console.log('handleHelpClick :: actions', actions);
  console.log('handleHelpClick :: currentStep', currentStep);

  // Create unique timestamp for this help request
  const timestamp = Date.now();

  // Add user message asking for help with a unique ID
  const userHelpMessage: UserMessage = {
    id: `user-help-${currentStep}-${timestamp}`,
    type: 'user',
    content: 'Offer me some suggestions for what I can write'
  };

  // Add AI response with suggestions with a unique ID
  const aiHelpMessage: HelpMessage = {
    id: `ai-help-${currentStep}-${timestamp}`,
    type: 'help',
    content: `Here are some ideas for answering the current question:`,
    suggestions: getWritingSuggestions(currentStep)
  };

  actions.setMessages((prev: Message[]) => [...prev, userHelpMessage, aiHelpMessage]);
  actions.setShowHelpSuggestions(true);
};

export const handleHelpSuggestionClick = (
  actions: ChatActions,
  suggestion: string
) => {

  console.log('handleHelpSuggestionClick :: actions', actions);
  console.log('handleHelpSuggestionClick :: suggestion', suggestion);

  actions.setUserInput(suggestion);
  if (actions.focusInput) {
    actions.focusInput();
  }
};

export const handleProceed = (
  actions: ChatActions
) => {

  console.log('handleProceed :: actions', actions);

  // Add final confirmation message from AI
  const proceedMessage: ProceedMessage = {
    id: 'proceed-confirmation',
    type: 'system',
    content: "Perfect! I'm generating your website now. This will just take a moment..."
  };

  actions.setMessages((prev: Message[]) => [...prev, proceedMessage]);
  actions.setShowCompletionButtons(false);

  // Here you would typically start the website generation process
  // For now we'll just show a confirmation message
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' bytes';
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  else return (bytes / 1048576).toFixed(1) + ' MB';
};

// Convert File to base64 data URL
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

// Check if file is a supported image type
export const isImageFile = (file: File): boolean => {
  const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  return supportedTypes.includes(file.type);
};

export const isImageSizeValid = (file: File): boolean => {
  return file.size <= MAX_IMAGE_UPLOAD_BYTES;
};

// Convert File to ImageData for chat
export const fileToImageData = async (file: File): Promise<ImageData> => {
  const base64 = await fileToBase64(file);
  return {
    url: base64,
    name: file.name,
    size: formatFileSize(file.size)
  };
};
